// dependencies
import { isAfter, isBefore, parseISO } from "date-fns";
import React from "react";

import { ICellRendererParams, Link, PYColumnDef, Text } from "@performyard/py-components";

//components
import TableChip from "../../../../../../components/Chip/templates/TableChip";

//types
import { Review } from "../../../../../../api/services/searchServiceResultTypes/reviews";

//utils
import get from "../../../../../../utils/GetNested";
import { toViewReviewUrl } from "../../../../../../utils/URLs/ViewReview";

const ViewButtonCellRenderer = (params: ICellRendererParams<Review>) => {
	const report = params.data;
	if (!report) return null;
	return (
		<div className="flex h-full items-center">
			<Link
				href={toViewReviewUrl(report._id)}
				underline="never"
				dangerouslySetClassName="inline-flex h-8 items-center justify-center rounded-1 border-none bg-primary-5 px-2 text-paragraph-3 font-semibold text-neutral-0 hover:bg-primary-7 active:bg-primary-8"
			>
				<Text size="paragraph-3">Open Review</Text>
			</Link>
		</div>
	);
};

const createReviewNameCellRenderer = (easyAddEnabled: boolean) => {
	const ReviewNameCellRenderer = (params: ICellRendererParams<Review>) => {
		const review = params.data;
		if (!review) return null;

		let name = get(review, ["name"], null);
		const bulkReviewName = get(review, ["bulk_review", "name"], null);

		if (bulkReviewName && bulkReviewName.length > 0) {
			name = `${name} (Part of ${bulkReviewName})`;
		}

		if (easyAddEnabled) {
			const is360Enabled = get(review, ["360_count"], false);
			const isAwaitingApproval = get(review, ["awaiting_approval_count"], false);

			return (
				<div className="flex h-full items-center justify-between gap-1">
					<Text size="paragraph-3" dangerouslySetClassName="py-line-clamp-1">
						{name}
					</Text>
					<div className="flex shrink-0 items-center gap-1">
						{is360Enabled ? <TableChip color="blue" label="360 Enabled" /> : null}
						{isAwaitingApproval ? <TableChip color="green" label="Awaiting Approval" /> : null}
					</div>
				</div>
			);
		}

		return (
			<div className="flex h-full items-center">
				<Text size="paragraph-3" dangerouslySetClassName="py-line-clamp-1">
					{name}
				</Text>
			</div>
		);
	};
	return ReviewNameCellRenderer;
};

const StatusCellRenderer = (params: ICellRendererParams<Review>) => {
	const review = params.data;
	if (!review) return null;

	const startDate = get(review, ["start_date"], null);
	const completed = get(review, ["completed"], false);
	const hasUnassignedReports = get(review, ["unassigned_report_form_count"], false);
	let status = "";

	const parsedDate = parseISO(startDate);
	if (completed) {
		status = "Completed";
	} else if (startDate && isAfter(parsedDate, new Date())) {
		status = hasUnassignedReports ? "Scheduled (Unassigned Forms)" : "Scheduled";
	} else if (startDate && isBefore(parsedDate, new Date())) {
		status = hasUnassignedReports ? "Active (Unassigned Forms)" : "Active";
	}

	return (
		<div className="flex h-full items-center">
			<Text size="paragraph-3" dangerouslySetClassName="line-clamp-1">
				{status}
			</Text>
		</div>
	);
};

export const ReviewsOfMeTableCols = (easyAddEnabled = false): PYColumnDef<Review>[] => {
	return [
		{
			headerName: "",
			field: "view",
			cellRenderer: ViewButtonCellRenderer,
			width: 116,
			sortable: false,
			resizable: false,
		},
		{
			headerName: "Review Name",
			field: "reviewName",
			cellRenderer: createReviewNameCellRenderer(easyAddEnabled),
			width: 640,
			sortable: false,
			resizable: false,
		},
		{
			headerName: "Start Date",
			field: "start_date",
			type: "date",
			width: 176,
			sortable: true,
			resizable: false,
			cellRendererParams: {
				dateFormat: "MMM d, yyyy h:mm a",
			},
		},
		{
			headerName: "Status",
			field: "status",
			cellRenderer: StatusCellRenderer,
			width: 160,
			sortable: false,
			resizable: false,
		},
	];
};

export default ReviewsOfMeTableCols;
